import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    GetPublicConfig,
    RegisterParticipant,
    StartGameSession,
    SubmitResult,
    TrackFunnelMetric,
    FormatTime,
    ExtractTrafficSource,
    GetStoredParticipant,
    SaveStoredParticipant,
    NormalizePhone,
    NormalizeEmail,
    resolveMediaUrl
} from '../../../utilities/BL/MemoryIndividual';

// =============================================================================
// TEXTOS LEGALES (LFPDPPP Y TÉRMINOS)
// =============================================================================

const TERMS_TEXT = `TÉRMINOS Y CONDICIONES DE LA DINÁMICA - MEMORAMA
1. Organizador: Dinámica organizada por el Club Atlético de San Luis para sus aficionados y público en general.
2. Deslinde Tecnológico: Five Digital Minds e Interaktia actúan estrictamente como proveedores de la infraestructura tecnológica de la plataforma y quedan eximidos de la entrega física de bienes y premios o resolución de controversias comerciales.
3. Participación: Dinámica válida para aficionados. Cada aficionado podrá registrarse y participar una sola vez por jornada o dinámica mediante su número celular y correo electrónico únicos.
4. Reglas del Juego y Criterio de Ganadores: El aficionado debe encontrar los 6 pares de cartas iguales (tablero 3x4 de 12 cartas) en el menor tiempo posible. Se premiará a los primeros 20 participantes del ranking oficial que hayan completado la dinámica en el menor tiempo y con el menor número de intentos válidos registrados en el sistema.
5. Premio y Restricciones (Cinépolis):
   a) Cada uno de los 20 ganadores recibirá un (1) pase doble para Cinépolis.
   b) Vigencia: Válido del 15 de Octubre de 2026 hasta el 31 de Diciembre de 2026.
   c) Días válidos: De lunes a domingo.
   d) Formato: Válido exclusivamente para funciones tradicionales en formato 2D.
   e) Sujeto a disponibilidad de cupo en salas y a las políticas generales de Cinépolis. No aplica en salas especiales (VIP, IMAX, 4DX, Macro XE) ni eventos especiales o festivales.
6. Validación Anti-Cheat: Tiempos inferiores al umbral humano físico mínimo o con anomalías de integridad técnica no serán computados como válidos en el ranking oficial.
7. Decisiones: Las determinaciones del comité organizador del Club Atlético de San Luis son definitivas e inapelables.`;

const PRIVACY_TEXT = `AVISO DE PRIVACIDAD SIMPLIFICADO - MEMORAMA
En cumplimiento estricto con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP), sus datos personales (Nombre completo, Correo electrónico y Teléfono celular) serán tratados por el Club Atlético de San Luis y la plataforma tecnológica Interaktia para las siguientes finalidades:
1. Validar su participación en la dinámica digital "Memorama".
2. Registrar su tiempo oficial y validar su identidad en caso de resultar ganador(a).
3. Brindarle atención, aclaraciones y seguimiento respecto a la premiación de la dinámica.

Sus datos personales están salvaguardados mediante rigurosas medidas de seguridad administrativas, técnicas y físicas. En cualquier momento puede ejercer sus derechos ARCO contactando a los organizadores oficiales.`;

// Cartas por defecto en caso de no haber tema configurado
const DEFAULT_CARDS = [
    { id: 1, pairId: 1, imageUrl: '/img/page/adsl-five.png', name: 'Carta 1' },
    { id: 2, pairId: 2, imageUrl: '/img/selfiefan/Adsl-FanCam.png', name: 'Carta 2' },
    { id: 3, pairId: 3, imageUrl: '/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif', name: 'Carta 3' },
    { id: 4, pairId: 4, imageUrl: '/img/page/adsl-five.png', name: 'Carta 4' },
    { id: 5, pairId: 5, imageUrl: '/img/selfiefan/Adsl-FanCam.png', name: 'Carta 5' },
    { id: 6, pairId: 6, imageUrl: '/img/selfiefan/Adsl-FanCam.png', name: 'Carta 6' },
];

const FALLBACK_BACK_IMAGE = '/img/selfiefan/Adsl-FanCam.png';

// =============================================================================
// MICRO-SINTETIZADOR DE AUDIO NATIVO (WEB AUDIO API)
// =============================================================================

const createAudioSynth = () => {
    let audioCtx = null;

    const getCtx = () => {
        if (typeof window === 'undefined') return null;
        if (!audioCtx) {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (AudioContextClass) {
                audioCtx = new AudioContextClass();
            }
        }
        if (audioCtx && audioCtx.state === 'suspended') {
            audioCtx.resume().catch(() => {});
        }
        return audioCtx;
    };

    return {
        playFlip: () => {
            try {
                const ctx = getCtx();
                if (!ctx) return;
                const now = ctx.currentTime;
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();

                osc.type = 'sine';
                osc.frequency.setValueAtTime(400, now);
                osc.frequency.exponentialRampToValueAtTime(200, now + 0.08);

                gain.gain.setValueAtTime(0.2, now);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.start(now);
                osc.stop(now + 0.08);
            } catch (e) {
                // Audio no disponible o silenciado por el navegador
            }
        },
        playMatch: () => {
            try {
                const ctx = getCtx();
                if (!ctx) return;
                const notes = [523.25, 659.25, 783.99]; // Acorde C5 - E5 - G5
                notes.forEach((freq, i) => {
                    const now = ctx.currentTime + (i * 0.09);
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();

                    osc.type = 'triangle';
                    osc.frequency.setValueAtTime(freq, now);

                    gain.gain.setValueAtTime(0.22, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

                    osc.connect(gain);
                    gain.connect(ctx.destination);

                    osc.start(now);
                    osc.stop(now + 0.22);
                });
            } catch (e) {
            }
        },
        playMismatch: () => {
            try {
                const ctx = getCtx();
                if (!ctx) return;
                const now = ctx.currentTime;
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();

                osc.type = 'sawtooth';
                osc.frequency.setValueAtTime(220, now);
                osc.frequency.exponentialRampToValueAtTime(160, now + 0.2);

                gain.gain.setValueAtTime(0.18, now);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.start(now);
                osc.stop(now + 0.2);
            } catch (e) {
            }
        },
        playVictory: () => {
            try {
                const ctx = getCtx();
                if (!ctx) return;
                const fanfare = [
                    { freq: 523.25, delay: 0, dur: 0.15 },
                    { freq: 659.25, delay: 0.12, dur: 0.15 },
                    { freq: 783.99, delay: 0.24, dur: 0.2 },
                    { freq: 1046.50, delay: 0.42, dur: 0.55 }
                ];
                fanfare.forEach(({ freq, delay, dur }) => {
                    const now = ctx.currentTime + delay;
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();

                    osc.type = 'triangle';
                    osc.frequency.setValueAtTime(freq, now);

                    gain.gain.setValueAtTime(0.25, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + dur);

                    osc.connect(gain);
                    gain.connect(ctx.destination);

                    osc.start(now);
                    osc.stop(now + dur);
                });
            } catch (e) {
            }
        }
    };
};

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

export const MemoryIndividualFan = () => {
    // Máquina de estados: 'register' | 'instructions' | 'countdown' | 'game' | 'sending' | 'done' | 'played' | 'inactive'
    const [step, setStep] = useState('register');

    // Datos del formulario
    const [userData, setUserData] = useState({
        fullName: '',
        email: '',
        phoneNumber: '',
        abonado: '',
        acceptedTerms: false
    });

    // Configuración y datos de sesión
    const [config, setConfig] = useState(null);
    const [participantData, setParticipantData] = useState({ participantId: null, sessionToken: '' });

    // Tablero y estado de juego
    const [board, setBoard] = useState([]);
    const [flippedIndices, setFlippedIndices] = useState([]);
    const [isLocked, setIsLocked] = useState(false);
    const [pairsFound, setPairsFound] = useState(0);
    const [attempts, setAttempts] = useState(0);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [finalFormattedTime, setFinalFormattedTime] = useState('00:00.000');

    // Controles de audio y UI
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [countdownNumber, setCountdownNumber] = useState(3);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Modales legales
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    // Referencias mutables
    const synthRef = useRef(null);
    const startTimeRef = useRef(0);
    const gameTimerRef = useRef(null);
    const countdownTimerRef = useRef(null);
    const trafficSourceRef = useRef('direct');
    const formStartTrackedRef = useRef(false);

    // Inicializar sintetizador de audio
    useEffect(() => {
        synthRef.current = createAudioSynth();
        return () => {
            if (gameTimerRef.current) clearInterval(gameTimerRef.current);
            if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
        };
    }, []);

    // Reproducción de sonido condicionada
    const playFlipSound = useCallback(() => {
        if (soundEnabled && synthRef.current) synthRef.current.playFlip();
    }, [soundEnabled]);

    const playMatchSound = useCallback(() => {
        if (soundEnabled && synthRef.current) synthRef.current.playMatch();
    }, [soundEnabled]);

    const playMismatchSound = useCallback(() => {
        if (soundEnabled && synthRef.current) synthRef.current.playMismatch();
    }, [soundEnabled]);

    const playVictorySound = useCallback(() => {
        if (soundEnabled && synthRef.current) synthRef.current.playVictory();
    }, [soundEnabled]);

    // Precarga de imágenes en segundo plano (Zero Lag)
    const preloadAssets = useCallback((cardsList, backImg) => {
        if (typeof window === 'undefined') return;
        try {
            if (backImg) {
                const img = new Image();
                img.src = resolveMediaUrl(backImg);
            }
            if (Array.isArray(cardsList)) {
                cardsList.forEach(c => {
                    if (c && c.imageUrl) {
                        const img = new Image();
                        img.src = resolveMediaUrl(c.imageUrl);
                    }
                });
            }
            const bannerImg = new Image();
            bannerImg.src = '/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif';
            const logoImg = new Image();
            logoImg.src = '/img/page/adsl-five.png';
        } catch (e) {
            console.warn('Advertencia no crítica al precargar recursos:', e);
        }
    }, []);

    // 1. Inicialización y consulta de configuración al montar
    useEffect(() => {
        const source = ExtractTrafficSource();
        trafficSourceRef.current = source;

        // Detectar si el aficionado ingresó escaneando el código QR (?s=qr, ?src=qr, ?utm_source=qr, ?source=qr, etc.)
        let isQr = source === 'qr_screen';
        if (!isQr && typeof window !== 'undefined') {
            try {
                const search = window.location.search || (window.location.hash.includes('?') ? window.location.hash.substring(window.location.hash.indexOf('?')) : '');
                const params = new URLSearchParams(search);
                const s = (params.get('s') || params.get('src') || params.get('utm_source') || params.get('source') || '').toLowerCase().trim();
                if (s === 'qr' || s === 'pantalla' || s === 'qr_screen' || s === 'screen_qr' || s === 'obs') {
                    isQr = true;
                }
            } catch (e) {
                // Safe ignore
            }
        }

        if (isQr) {
            TrackFunnelMetric('qr_scan', source || 'qr_screen');
        }

        TrackFunnelMetric('page_view', source);

        const init = async () => {
            try {
                // Revisar sesión persistente previa
                const stored = GetStoredParticipant();
                if (stored) {
                    if (stored.previousTime || stored.completed) {
                        setFinalFormattedTime(stored.previousTime || stored.formattedTime || '00:00.000');
                        setStep('played');
                        return;
                    }
                    if (stored.participantId && stored.sessionToken) {
                        setUserData({
                            fullName: stored.name || '',
                            email: stored.email || '',
                            phoneNumber: stored.phone || '',
                            abonado: stored.abonado ? (stored.abonado.toLowerCase() === 'sí' || stored.abonado.toLowerCase() === 'si' ? 'si' : 'no') : (stored.isAbonado ? 'si' : ''),
                            acceptedTerms: true
                        });
                        setParticipantData({
                            participantId: stored.participantId,
                            sessionToken: stored.sessionToken
                        });
                        setStep('instructions');
                    }
                }

                // Consultar configuración pública
                const res = await GetPublicConfig();
                if (res && res.success && res.data) {
                    const cfg = res.data;
                    setConfig(cfg);

                    const isActive = cfg.isDynamicActive !== undefined ? cfg.isDynamicActive : (cfg.IsDynamicActive !== undefined ? cfg.IsDynamicActive : true);
                    if (!isActive) {
                        setStep('inactive');
                        return;
                    }

                    // Precargar imágenes en memoria
                    const cards = cfg.cards || cfg.Cards || DEFAULT_CARDS;
                    const backImage = cfg.cardBackImage || cfg.CardBackImage || FALLBACK_BACK_IMAGE;
                    preloadAssets(cards, backImage);
                }
            } catch (err) {
                console.error('Error al inicializar MemoryIndividualFan:', err);
            }
        };

        init();
    }, [preloadAssets]);

    // Tracking de form_start en primer foco
    const handleInputFocus = () => {
        if (!formStartTrackedRef.current) {
            formStartTrackedRef.current = true;
            TrackFunnelMetric('form_start', trafficSourceRef.current);
        }
    };

    // Manejo de cambios en el formulario
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setUserData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setErrorMessage('');
    };

    // Envío del formulario de registro
    const handleRegister = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        const cleanName = userData.fullName.trim();
        if (!cleanName || cleanName.length < 2) {
            setErrorMessage('Por favor escribe tu nombre completo.');
            return;
        }

        const normalizedEmail = NormalizeEmail(userData.email);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!normalizedEmail || !emailRegex.test(normalizedEmail)) {
            setErrorMessage('Por favor ingresa un correo electrónico válido.');
            return;
        }

        const normalizedPhone = NormalizePhone(userData.phoneNumber);
        if (!normalizedPhone || normalizedPhone.length !== 10) {
            setErrorMessage('Por favor ingresa un número de celular válido a 10 dígitos.');
            return;
        }

        if (!userData.abonado || (userData.abonado !== 'si' && userData.abonado !== 'no')) {
            setErrorMessage('Por favor indica si eres abonado o no.');
            return;
        }

        if (!userData.acceptedTerms) {
            setErrorMessage('Debes aceptar los Términos y Condiciones para participar.');
            return;
        }

        setIsLoading(true);

        try {
            const res = await RegisterParticipant({
                name: cleanName,
                email: normalizedEmail,
                phone: normalizedPhone,
                abonado: userData.abonado === 'si' ? 'Sí' : 'No',
                isAbonado: userData.abonado === 'si',
                acceptedTerms: true,
                source: trafficSourceRef.current
            });

            if (res.code === 'ALREADY_PLAYED') {
                setFinalFormattedTime(res.previousTime || res.formattedTime || '00:00.000');
                setStep('played');
                return;
            }

            if (!res.success) {
                setErrorMessage(res.message || 'No se pudo completar el registro. Inténtalo nuevamente.');
                return;
            }

            setParticipantData({
                participantId: res.participantId,
                sessionToken: res.sessionToken
            });

            setStep('instructions');
        } catch (err) {
            console.error('Error en registro:', err);
            setErrorMessage('Error de conexión al registrar tus datos. Por favor reintenta.');
        } finally {
            setIsLoading(false);
        }
    };

    // Barajado Fisher-Yates de los 6 pares (12 cartas)
    const buildShuffledDeck = useCallback(() => {
        const rawCards = (config && (config.cards || config.Cards)) || DEFAULT_CARDS;
        const baseCards = rawCards.length >= 6 ? rawCards.slice(0, 6) : DEFAULT_CARDS;
        const deck = [];

        baseCards.forEach((card, idx) => {
            const pairId = card.pairId !== undefined ? card.pairId : (idx + 1);
            const rawImageUrl = card.imageUrl || '/img/page/adsl-five.png';
            const imageUrl = resolveMediaUrl(rawImageUrl);

            deck.push({
                id: `card_${pairId}_a`,
                pairId,
                imageUrl,
                isFlipped: false,
                isMatched: false
            });
            deck.push({
                id: `card_${pairId}_b`,
                pairId,
                imageUrl,
                isFlipped: false,
                isMatched: false
            });
        });

        // Fisher-Yates
        for (let i = deck.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            const temp = deck[i];
            deck[i] = deck[j];
            deck[j] = temp;
        }

        return deck;
    }, [config]);

    // Arrancar el juego tras cuenta regresiva
    const initGame = useCallback(() => {
        const newDeck = buildShuffledDeck();
        setBoard(newDeck);
        setFlippedIndices([]);
        setPairsFound(0);
        setAttempts(0);
        setIsLocked(false);
        setElapsedMs(0);

        startTimeRef.current = performance.now();

        if (gameTimerRef.current) clearInterval(gameTimerRef.current);
        gameTimerRef.current = setInterval(() => {
            const now = performance.now();
            const diff = Math.max(0, Math.round(now - startTimeRef.current));
            setElapsedMs(diff);
        }, 50);

        setStep('game');
    }, [buildShuffledDeck]);

    // Iniciar cuenta regresiva 3..2..1
    const handleStartCountdown = async () => {
        setIsLoading(true);
        try {
            if (participantData.participantId && participantData.sessionToken) {
                await StartGameSession(
                    participantData.participantId,
                    participantData.sessionToken,
                    trafficSourceRef.current
                );
            }
        } catch (err) {
            console.warn('Fallo no bloqueante en StartGameSession:', err);
        } finally {
            setIsLoading(false);
        }

        setStep('countdown');
        setCountdownNumber(3);

        countdownTimerRef.current = setTimeout(() => {
            setCountdownNumber(2);
            countdownTimerRef.current = setTimeout(() => {
                setCountdownNumber(1);
                countdownTimerRef.current = setTimeout(() => {
                    setCountdownNumber('¡A JUGAR!');
                    countdownTimerRef.current = setTimeout(() => {
                        initGame();
                    }, 800);
                }, 1000);
            }, 1000);
        }, 1000);
    };

    // Manejo de victoria
    const handleVictory = useCallback(async (finalAttempts) => {
        if (gameTimerRef.current) {
            clearInterval(gameTimerRef.current);
            gameTimerRef.current = null;
        }

        const totalMs = Math.max(0, Math.round(performance.now() - startTimeRef.current));
        const formatted = FormatTime(totalMs);
        setElapsedMs(totalMs);
        setFinalFormattedTime(formatted);
        playVictorySound();

        // 750ms de pausa de celebración para ver el tablero resuelto
        setTimeout(async () => {
            setStep('sending');
            try {
                await SubmitResult({
                    participantId: participantData.participantId,
                    sessionToken: participantData.sessionToken,
                    clientTimeMs: totalMs,
                    formattedTime: formatted,
                    attempts: finalAttempts,
                    themeId: config?.activeThemeId || config?.ActiveThemeId || null,
                    source: trafficSourceRef.current
                });

                SaveStoredParticipant({
                    participantId: participantData.participantId,
                    sessionToken: participantData.sessionToken,
                    name: userData.fullName,
                    email: userData.email,
                    phone: userData.phoneNumber,
                    previousTime: formatted,
                    formattedTime: formatted,
                    completed: true,
                    totalMs
                });
            } catch (err) {
                console.error('Error al enviar resultado final:', err);
            } finally {
                setStep('done');
            }
        }, 750);
    }, [config, participantData, playVictorySound, userData]);

    // Toque sobre una carta
    const handleCardClick = (index) => {
        if (isLocked) return;
        if (!board[index] || board[index].isFlipped || board[index].isMatched) return;
        if (flippedIndices.length >= 2) return;

        playFlipSound();

        const newBoard = [...board];
        newBoard[index] = { ...newBoard[index], isFlipped: true };
        setBoard(newBoard);

        const newFlipped = [...flippedIndices, index];
        setFlippedIndices(newFlipped);

        if (newFlipped.length === 2) {
            const firstIdx = newFlipped[0];
            const secondIdx = newFlipped[1];
            const firstCard = newBoard[firstIdx];
            const secondCard = newBoard[secondIdx];

            const nextAttempts = attempts + 1;
            setAttempts(nextAttempts);

            if (firstCard.pairId === secondCard.pairId) {
                // ¡COINCIDENCIA!
                playMatchSound();
                newBoard[firstIdx] = { ...newBoard[firstIdx], isMatched: true };
                newBoard[secondIdx] = { ...newBoard[secondIdx], isMatched: true };
                setBoard(newBoard);
                setFlippedIndices([]);

                const nextPairs = pairsFound + 1;
                setPairsFound(nextPairs);

                if (nextPairs === 6) {
                    handleVictory(nextAttempts);
                }
            } else {
                // ¡MISMATCH!
                setIsLocked(true);
                playMismatchSound();
                setTimeout(() => {
                    setBoard(currBoard => {
                        const resetBoard = [...currBoard];
                        if (resetBoard[firstIdx] && !resetBoard[firstIdx].isMatched) {
                            resetBoard[firstIdx] = { ...resetBoard[firstIdx], isFlipped: false };
                        }
                        if (resetBoard[secondIdx] && !resetBoard[secondIdx].isMatched) {
                            resetBoard[secondIdx] = { ...resetBoard[secondIdx], isFlipped: false };
                        }
                        return resetBoard;
                    });
                    setFlippedIndices([]);
                    setIsLocked(false);
                }, 750);
            }
        }
    };

    // Render de modales legales
    const renderLegalModal = (title, content, setter) => {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-75 d-flex align-items-center justify-content-center px-3" style={{ zIndex: 11000 }}>
                <div className="card w-100 bg-white border-0 rounded-4 shadow-lg p-4" style={{ maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h4 className="fw-bold text-dark mb-0 fs-5">{title}</h4>
                        <button type="button" className="btn-close" onClick={() => setter(false)}></button>
                    </div>
                    <div className="text-secondary small" style={{ whiteSpace: 'pre-wrap', textAlign: 'justify', lineHeight: '1.5' }}>
                        {content}
                    </div>
                    <button type="button" className="btn btn-danger w-100 py-2 rounded-3 fw-bold mt-4" onClick={() => setter(false)}>
                        Cerrar
                    </button>
                </div>
            </div>
        );
    };

    const rawCardBackImage = (config && (config.cardBackImage || config.CardBackImage)) || FALLBACK_BACK_IMAGE;
    const cardBackImage = resolveMediaUrl(rawCardBackImage);

    return (
        <div className="MemoryIndividualFan" style={{ backgroundImage: 'url("/img/page/backgrounds/barrio9/background-1.png")' }}>
            {showTermsModal && renderLegalModal('Términos y Condiciones', TERMS_TEXT, setShowTermsModal)}
            {showPrivacyModal && renderLegalModal('Aviso de Privacidad', PRIVACY_TEXT, setShowPrivacyModal)}

            {/* FASE 1: REGISTRO */}
            {step === 'register' && (
                <div className="MemoryIndividualFan__Container">
                    <header className="MemoryIndividualFan__Header">
                        <img src="/img/page/adsl-five.png" alt="ADSL&Interaktia" className="MemoryIndividualFan__Logo" />
                        <h1 className="MemoryIndividualFan__Title">MEMORAMA</h1>
                        <p className="MemoryIndividualFan__Subtitle">
                            Encuentra los 6 pares de cartas en el menor tiempo posible y podrías ganar pases dobles para Cinépolis.
                        </p>
                    </header>

                    <form onSubmit={handleRegister} className="MemoryIndividualFan__CardForm" style={{ backgroundColor: '#0b132b' }}>
                        {errorMessage && (
                            <div className="alert alert-danger py-2 px-3 small rounded-3 mb-3" role="alert">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                {errorMessage}
                            </div>
                        )}

                        <div>
                            <label htmlFor="fullName" className="MemoryIndividualFan__FormLabel">
                                Nombre completo
                            </label>
                            <input
                                id="fullName"
                                name="fullName"
                                type="text"
                                value={userData.fullName}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                //placeholder="Escribe tu nombre y apellido"
                                className="MemoryIndividualFan__FormInput"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="email" className="MemoryIndividualFan__FormLabel">
                                Correo electrónico
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                value={userData.email}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                //placeholder="ejemplo@correo.com"
                                className="MemoryIndividualFan__FormInput"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="phoneNumber" className="MemoryIndividualFan__FormLabel">
                                Teléfono celular (10 dígitos)
                            </label>
                            <input
                                id="phoneNumber"
                                name="phoneNumber"
                                type="tel"
                                maxLength={10}
                                value={userData.phoneNumber}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                //placeholder="4441234567"
                                className="MemoryIndividualFan__FormInput"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="abonado" className="MemoryIndividualFan__FormLabel">
                                ¿Eres abonado?
                            </label>
                            <select
                                id="abonado"
                                name="abonado"
                                value={userData.abonado}
                                onChange={handleInputChange}
                                className="MemoryIndividualFan__FormInput"
                                style={{ cursor: 'pointer', backgroundColor: '#0b132b', color: '#ffffff' }}
                                required
                            >
                                <option value="" disabled style={{ backgroundColor: '#0b132b', color: '#94a3b8' }}>
                                    Selecciona una opción
                                </option>
                                <option value="si" style={{ backgroundColor: '#0b132b', color: '#ffffff' }}>
                                    Sí
                                </option>
                                <option value="no" style={{ backgroundColor: '#0b132b', color: '#ffffff' }}>
                                    No
                                </option>
                            </select>
                        </div>

                        <div className="form-check my-3 text-start d-flex align-items-start gap-2">
                            <input
                                className="form-check-input mt-1"
                                type="checkbox"
                                id="acceptedTerms"
                                name="acceptedTerms"
                                checked={userData.acceptedTerms}
                                onChange={handleInputChange}
                                style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer', flexShrink: 0 }}
                            />
                            <label className="form-check-label text-light small" htmlFor="acceptedTerms" style={{ fontSize: '0.8rem', lineHeight: '1.4', cursor: 'pointer' }}>
                                He leído y acepto los{' '}
                                <button type="button" className="btn btn-link p-0 text-danger border-0 align-baseline" style={{ fontSize: '0.8rem', fontWeight: 'bold' }} onClick={() => setShowTermsModal(true)}>
                                    Términos y Condiciones
                                </button>{' '}
                                y el{' '}
                                <button type="button" className="btn btn-link p-0 text-danger border-0 align-baseline" style={{ fontSize: '0.8rem', fontWeight: 'bold' }} onClick={() => setShowPrivacyModal(true)}>
                                    Aviso de Privacidad
                                </button>{' '}
                                de la dinámica.
                            </label>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="MemoryIndividualFan__ButtonPrimary mt-3"
                        >
                            {isLoading ? (
                                <span><i className="bi bi-hourglass-split me-2"></i>Registrando...</span>
                            ) : (
                                <span><i className="bi bi-play-circle-fill me-2"></i>REGISTRARME Y JUGAR</span>
                            )}
                        </button>
                    </form>

                    <footer className="text-center mt-4 FooterCredits small">
                        Hecho con ♥ por Interaktia
                    </footer>
                </div>
            )}

            {/* FASE 2: INSTRUCCIONES */}
            {step === 'instructions' && (
                <div className="MemoryIndividualFan__Container">
                    <header className="MemoryIndividualFan__Header">
                        <img src="/img/page/adsl-five.png" alt="Atlético de San Luis" className="MemoryIndividualFan__Logo" />
                        <h2 className="MemoryIndividualFan__Title">¡LISTO PARA JUGAR!</h2>
                        <p className="MemoryIndividualFan__Subtitle">
                            Bienvenid@ {userData.fullName || 'Aficionad@'}
                        </p>
                    </header>

                    <div className="MemoryIndividualFan__InstructionsCard" style={{ backgroundColor: '#0b132b' }}>
                        <i className="bi bi-controller fs-1 text-danger d-block mb-2"></i>
                        <h3 className="fs-5 fw-bold text-light mb-3">¿Cómo jugar?</h3>

                        <div className="MemoryIndividualFan__InstructionsHighlight">
                            <i className="bi bi-lightning-charge-fill me-2"></i>
                            Haz tap en las cartas para abrirlas y encontrar los pares iguales. ¡El aficionado más rápido gana!
                        </div>

                        <ul className="text-start small text-light ps-3 mb-4" style={{ lineHeight: '1.7' }}>
                            <li>Hay 12 cartas boca abajo formando 6 pares.</li>
                            <li>Toca 2 cartas para descubrirlas. Si coinciden, quedarán abiertas.</li>
                            <li>Si no coinciden, volverán a voltearse rápidamente.</li>
                            <li>El cronómetro arranca inmediatamente tras la cuenta regresiva.</li>
                        </ul>

                        <button
                            type="button"
                            onClick={handleStartCountdown}
                            disabled={isLoading}
                            className="MemoryIndividualFan__ButtonSuccess"
                        >
                            <i className="bi bi-play-fill fs-4 me-2 align-middle"></i>
                            INICIAR JUEGO
                        </button>
                    </div>

                    <footer className="text-center mt-4 FooterCredits small">
                        Hecho con ♥ por Interaktia
                    </footer>
                </div>
            )}

            {/* FASE 3: CUENTA REGRESIVA */}
            {step === 'countdown' && (
                <div className="MemoryIndividualFan__CountdownOverlay">
                    <div className="MemoryIndividualFan__CountdownNumber">
                        {countdownNumber}
                    </div>
                    <div className="MemoryIndividualFan__CountdownText">
                        {countdownNumber === '¡A JUGAR!' ? '¡Demuestra tu memoria!' : 'Prepárate para comenzar'}
                    </div>
                </div>
            )}

            {/* FASE 4: JUEGO ACTIVO */}
            {step === 'game' && (
                <div className="MemoryIndividualFan__Container">
                    {/* HUD Superior */}
                    <div className="MemoryIndividualFan__HUD">
                        <div className="MemoryIndividualFan__HUDTimer">
                            <i className="bi bi-stopwatch me-2 text-info"></i>
                            {FormatTime(elapsedMs)}
                        </div>

                        <div className="MemoryIndividualFan__HUDStat">
                            Pares: <span className="text-warning fw-bold">{pairsFound}</span> de 6
                        </div>

                        <div className="MemoryIndividualFan__HUDStat">
                            Intentos: <span className="text-info fw-bold">{attempts}</span>
                        </div>

                        <button
                            type="button"
                            onClick={() => setSoundEnabled(prev => !prev)}
                            className="MemoryIndividualFan__HUDAudioBtn"
                            aria-label={soundEnabled ? 'Silenciar sonido' : 'Activar sonido'}
                            title={soundEnabled ? 'Silenciar sonido' : 'Activar sonido'}
                        >
                            <i className={soundEnabled ? 'bi bi-volume-up-fill text-warning' : 'bi bi-volume-mute-fill text-secondary'}></i>
                        </button>
                    </div>

                    {/* Tablero 3x4 Acelerado por GPU */}
                    <div className="MemoryIndividualFan__BoardGrid">
                        {board.map((card, idx) => {
                            const isFlipped = card.isFlipped || card.isMatched;
                            return (
                                <div
                                    key={card.id}
                                    onClick={() => handleCardClick(idx)}
                                    className={`MemoryIndividualFan__Card ${isFlipped ? 'MemoryIndividualFan__Card--Flipped' : ''} ${card.isMatched ? 'MemoryIndividualFan__Card--Matched' : ''}`}
                                >
                                    <div className="MemoryIndividualFan__CardInner">
                                        {/* Cara Trasera (Dorso) */}
                                        <div className="MemoryIndividualFan__CardFace MemoryIndividualFan__CardFace--Back">
                                            <img
                                                src={cardBackImage}
                                                alt="Reverso de carta"
                                                className="MemoryIndividualFan__CardImg"
                                            />
                                        </div>

                                        {/* Cara Frontal (Imagen del Tema) */}
                                        <div className="MemoryIndividualFan__CardFace MemoryIndividualFan__CardFace--Front">
                                            <img
                                                src={card.imageUrl}
                                                alt="Carta de memorama"
                                                className="MemoryIndividualFan__CardImg"
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <footer className="text-center mt-3 FooterCredits small">
                        Hecho con ♥ por Interaktia
                    </footer>
                </div>
            )}

            {/* FASE 5: ENVIANDO RESULTADO */}
            {step === 'sending' && (
                <div className="MemoryIndividualFan__Container text-center py-5">
                    <img src="/img/page/adsl-five.png" alt="Logo" className="MemoryIndividualFan__Logo mb-4" />
                    <div className="spinner-border text-danger mb-4" style={{ width: '3.5rem', height: '3.5rem' }} role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                    <h2 className="fs-4 fw-bold text-light mb-2">¡Partida terminada!</h2>
                    <p className="text-light mb-1">Un momento... estamos registrando tu tiempo oficial.</p>
                    <p className="small text-secondary">No cierres ni recargues esta pantalla.</p>

                    <footer className="text-center mt-5 FooterCredits small">
                        Hecho con ♥ por Interaktia
                    </footer>
                </div>
            )}

            {/* FASE 6: FINALIZADO EXITOSAMENTE (PANTALLA ESTÁNDAR ROOSTER) */}
            {step === 'done' && (
                <div className="MemoryIndividualFan__Container text-center">
                    <img src="/img/page/adsl-five.png" alt="Logo" className="MemoryIndividualFan__Logo mt-3" />

                    <div className="MemoryIndividualFan__DoneCard mt-3" style={{ backgroundColor: '#0b132b' }}>
                        <p className="fs-1 mb-1 text-success">
                            <i className="bi bi-check-circle-fill"></i>
                        </p>
                        <h2 className="fs-3 fw-bold text-light mb-1">¡Muy bien!</h2>
                        <p className="text-light mb-3">Tu participación ha sido enviada correctamente</p>

                        <div className="MemoryIndividualFan__TimeBadge">
                            <div className="small text-uppercase text-secondary fw-bold">Resolviste el memorama en:</div>
                            <div className="MemoryIndividualFan__TimeValue">{finalFormattedTime}</div>
                            <div className="MemoryIndividualFan__AttemptsValue">Intentos: {attempts}</div>
                        </div>

                        <p className="small text-warning mb-0">
                            <i className="bi bi-info-circle me-1"></i>
                            Ojo: en caso de resultar ganador(a), nos comunicaremos contigo.
                        </p>
                    </div>

                    <hr className="my-4 border-secondary" />

                    {/* Banner publicitario */}
                    <a
                        href="https://interaktia.com/?utm_source=memory_web&utm_medium=banner&utm_campaign=fdm_adsl"
                        target="_blank"
                        rel="noreferrer"
                        className="SelfieFan__Sent__Banner d-block mb-3"
                    >
                        <img
                            src="/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif"
                            alt="Publicidad"
                            className="d-block m-auto rounded-3 img-fluid shadow-sm"
                        />
                    </a>
                    <a
                        href="https://interaktia.com/?utm_source=memory_web&utm_medium=banner&utm_campaign=fdm_adsl"
                        target="_blank"
                        rel="noreferrer"
                        className="Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block mb-4"
                    >
                        Contactar ahora
                    </a>

                    <hr className="my-4 border-secondary" />

                    <p className="text-center text-light mb-0">¿Quieres conocer más de esta tecnología?</p>
                    <p className="text-center text-secondary mb-3">Interaktia está listo para ti</p>
                    <a
                        href="https://interaktia.com/?utm_source=memory_web&utm_medium=banner_text&utm_campaign=fdm_adsl"
                        target="_blank"
                        rel="noreferrer"
                        className="Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block"
                    >
                        Contactar ahora
                    </a>

                    <footer className="text-center mt-4 FooterCredits small">
                        Hecho con ♥ por Interaktia
                    </footer>
                </div>
            )}

            {/* FASE 7: YA PARTICIPÓ */}
            {step === 'played' && (
                <div className="MemoryIndividualFan__Container text-center">
                    <img src="/img/page/adsl-five.png" alt="Logo" className="MemoryIndividualFan__Logo mt-3" />

                    <div className="MemoryIndividualFan__DoneCard mt-3" style={{ backgroundColor: '#0b132b' }}>
                        <p className="fs-1 mb-1 text-success">
                            <i className="bi bi-check-circle-fill"></i>
                        </p>
                        <h2 className="fs-3 fw-bold text-light mb-2">Gracias por tu participación</h2>
                        <p className="text-light mb-3">Ya registraste tu tiempo en este partido.</p>

                        {finalFormattedTime && finalFormattedTime !== '00:00.000' && (
                            <div className="MemoryIndividualFan__TimeBadge">
                                <div className="small text-uppercase text-secondary fw-bold">Tu tiempo registrado:</div>
                                <div className="MemoryIndividualFan__TimeValue">{finalFormattedTime}</div>
                            </div>
                        )}

                        <p className="small text-warning mb-0">
                            <i className="bi bi-info-circle me-1"></i>
                            Ojo: en caso de resultar ganador(a), nos comunicaremos contigo.
                        </p>
                    </div>

                    <hr className="my-4 border-secondary" />

                    {/* Banner publicitario */}
                    <a
                        href="https://interaktia.com/?utm_source=memory_web&utm_medium=banner&utm_campaign=fdm_adsl"
                        target="_blank"
                        rel="noreferrer"
                        className="SelfieFan__Sent__Banner d-block mb-3"
                    >
                        <img
                            src="/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif"
                            alt="Publicidad"
                            className="d-block m-auto rounded-3 img-fluid shadow-sm"
                        />
                    </a>
                    <a
                        href="https://interaktia.com/?utm_source=memory_web&utm_medium=banner&utm_campaign=fdm_adsl"
                        target="_blank"
                        rel="noreferrer"
                        className="Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block mb-4"
                    >
                        Contactar ahora
                    </a>

                    <hr className="my-4 border-secondary" />

                    <p className="text-center text-light mb-0">¿Quieres conocer más de esta tecnología?</p>
                    <p className="text-center text-secondary mb-3">Interaktia está listo para ti</p>
                    <a
                        href="https://interaktia.com/?utm_source=memory_web&utm_medium=banner_text&utm_campaign=fdm_adsl"
                        target="_blank"
                        rel="noreferrer"
                        className="Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block"
                    >
                        Contactar ahora
                    </a>

                    <footer className="text-center mt-4 FooterCredits small">
                        Hecho con ♥ por Interaktia
                    </footer>
                </div>
            )}

            {/* FASE 8: DINÁMICA INACTIVA */}
            {step === 'inactive' && (
                <div className="MemoryIndividualFan__Container text-center py-5">
                    <img src="/img/page/adsl-five.png" alt="Logo" className="MemoryIndividualFan__Logo mb-4" />
                    <p className="fs-1 mb-2 text-warning">
                        <i className="bi bi-pause-circle-fill"></i>
                    </p>
                    <h2 className="fs-3 fw-bold text-light mb-2">Dinámica no disponible</h2>
                    <p className="text-light mb-2">La dinámica de Memorama no se encuentra activa en este momento.</p>
                    <p className="small text-secondary">
                        Mantente atento a la pantalla del estadio para las próximas dinámicas y premios.
                    </p>

                    <hr className="my-4 border-secondary" />

                    <a
                        href="https://interaktia.com/?utm_source=memory_web&utm_medium=banner&utm_campaign=fdm_adsl"
                        target="_blank"
                        rel="noreferrer"
                        className="SelfieFan__Sent__Banner d-block mb-3"
                    >
                        <img
                            src="/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif"
                            alt="Publicidad"
                            className="d-block m-auto rounded-3 img-fluid shadow-sm"
                        />
                    </a>
                    <a
                        href="https://interaktia.com/?utm_source=memory_web&utm_medium=banner&utm_campaign=fdm_adsl"
                        target="_blank"
                        rel="noreferrer"
                        className="Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block mb-4"
                    >
                        Contactar ahora
                    </a>

                    <footer className="text-center mt-5 FooterCredits small">
                        Hecho con ♥ por Interaktia
                    </footer>
                </div>
            )}
        </div>
    );
};

export default MemoryIndividualFan;
